import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { CodInvoiceSelection, CodSelectedInvoice } from "../../../types/codSalesRep";
import { formatCodAmount, nearlyEqualMoney, sumInvoiceRemaining } from "../../../utils/codUtils";
import { NJIM, primaryBtnSx, secondaryBtnSx } from "./codStyles";

interface PostCodPaymentModalProps {
  open: boolean;
  /** The Sage invoices this cash collection pays (one row each). */
  invoices: CodSelectedInvoice[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (documentReceiptNumber: string, selections: CodInvoiceSelection[]) => void;
  /** Optional: drop an invoice from this posting. */
  onRemoveInvoice?: (invoiceId: string) => void;
}

/** Same tolerance the backend uses when comparing money. */
const MONEY_EPS = 0.005;

const PostCodPaymentModal: React.FC<PostCodPaymentModalProps> = ({
  open,
  invoices,
  loading,
  onClose,
  onSubmit,
  onRemoveInvoice,
}) => {
  const { t } = useTranslation();
  const [receiptNumber, setReceiptNumber] = useState("");
  const [amountInputs, setAmountInputs] = useState<Record<string, string>>({});

  // Each amount starts at the invoice's remaining amount (= pay in full).
  useEffect(() => {
    if (!open) return;
    setReceiptNumber("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setAmountInputs((prev) => {
      const next: Record<string, string> = {};
      invoices.forEach((invoice) => {
        next[invoice.invoiceId] = prev[invoice.invoiceId] ?? String(invoice.remainingAmount);
      });
      return next;
    });
  }, [open, invoices]);

  const parsedAmounts = useMemo(() => {
    const map: Record<string, number> = {};
    invoices.forEach((invoice) => {
      map[invoice.invoiceId] = Number(amountInputs[invoice.invoiceId]);
    });
    return map;
  }, [amountInputs, invoices]);

  // Rules (match the backend): amount > 0 and ≤ the invoice's remainingAmount.
  const validationError = useMemo(() => {
    for (const invoice of invoices) {
      const entered = parsedAmounts[invoice.invoiceId];
      if (!Number.isFinite(entered) || entered <= MONEY_EPS) {
        return t("codSalesRep.amountMustBePositive", { invoice: invoice.invoiceNumber });
      }
      if (entered > invoice.remainingAmount + MONEY_EPS) {
        return t("codSalesRep.amountOverInvoiceBalance", {
          invoice: invoice.invoiceNumber,
          balance: formatCodAmount(invoice.remainingAmount, invoice.currency),
        });
      }
    }
    return "";
  }, [invoices, parsedAmounts, t]);

  const collectTotal = invoices.reduce((sum, invoice) => {
    const value = parsedAmounts[invoice.invoiceId];
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const fullTotal = sumInvoiceRemaining(invoices);
  const currency = invoices[0]?.currency ?? "IQD";

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = () => {
    if (loading || !receiptNumber.trim() || validationError || invoices.length === 0) return;
    const selections: CodInvoiceSelection[] = invoices.map((invoice) => {
      const entered = parsedAmounts[invoice.invoiceId];
      // Amount left out = pay the invoice's whole remainingAmount.
      return nearlyEqualMoney(entered, invoice.remainingAmount)
        ? { invoiceId: invoice.invoiceId }
        : { invoiceId: invoice.invoiceId, amount: entered };
    });
    onSubmit(receiptNumber.trim(), selections);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: (e: React.FormEvent) => {
          e.preventDefault();
          handleSubmit();
        },
        sx: { borderRadius: "16px", boxShadow: NJIM.elevationLg },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: NJIM.ink }}>
        {t("codSalesRep.postCodPayment")}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "12px",
            bgcolor: NJIM.muted,
            border: `1px solid ${NJIM.border}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, fontWeight: 600 }}>
            {t("codSalesRep.amountToCollect")}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: NJIM.ink, fontVariantNumeric: "tabular-nums" }}>
            {formatCodAmount(collectTotal, currency)}
          </Typography>
          {!nearlyEqualMoney(collectTotal, fullTotal) && (
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
              {t("codSalesRep.invoicesTotalRemaining")}: {formatCodAmount(fullTotal, currency)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("codSalesRep.postInvoicesHint", { count: invoices.length })}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          {invoices.map((invoice) => {
            const entered = parsedAmounts[invoice.invoiceId];
            const isPartial =
              Number.isFinite(entered) && entered > MONEY_EPS && entered < invoice.remainingAmount - MONEY_EPS;
            return (
              <Box key={invoice.invoiceId} sx={{ py: 1.25, borderBottom: `1px solid ${NJIM.border}` }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} color={NJIM.ink}>
                      {t("codSalesRep.invoiceNo", { number: invoice.invoiceNumber })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {invoice.lineReference || invoice.orderNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ textAlign: "end" }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("codSalesRep.invoiceRemaining")}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCodAmount(invoice.remainingAmount, invoice.currency)}
                      </Typography>
                    </Box>
                    {onRemoveInvoice && invoices.length > 1 && (
                      <Tooltip title={t("codSalesRep.removeInvoice")}>
                        <IconButton
                          size="small"
                          aria-label={t("codSalesRep.removeInvoice")}
                          onClick={() => onRemoveInvoice(invoice.invoiceId)}
                          disabled={loading}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t("codSalesRep.amountToCollect")}
                  helperText={isPartial ? t("codSalesRep.partialPayment") : t("codSalesRep.payInFull")}
                  value={amountInputs[invoice.invoiceId] ?? ""}
                  onChange={(e) =>
                    setAmountInputs((prev) => ({ ...prev, [invoice.invoiceId]: e.target.value }))
                  }
                  disabled={loading}
                  inputProps={{ min: 0, max: invoice.remainingAmount, step: "0.01" }}
                />
              </Box>
            );
          })}
        </Box>

        {validationError && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {validationError}
          </Alert>
        )}

        <TextField
          fullWidth
          required
          label={t("codSalesRep.documentReceiptNumber")}
          placeholder="R-55120"
          value={receiptNumber}
          onChange={(e) => setReceiptNumber(e.target.value)}
          disabled={loading}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          position: "sticky",
          bottom: 0,
          bgcolor: NJIM.white,
          borderTop: `1px solid ${NJIM.border}`,
        }}
      >
        <Button type="button" onClick={handleClose} disabled={loading} sx={secondaryBtnSx}>
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !receiptNumber.trim() || invoices.length === 0 || Boolean(validationError)}
          sx={primaryBtnSx}
        >
          {loading ? t("common.loading") : t("codSalesRep.confirmPayment")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostCodPaymentModal;
